import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from './redux-hooks';
import {
    addItem,
    clearCart,
    removeItem,
    setCartMenu,
    updateQuantity,
} from '../../store/redux/cartSlice';

// Single entry point to the cart for every component that touches it, so
// none of them reach into the store shape or repeat the price parsing.

export interface OrderableDish {
    id: number;
    name: string;
    price?: string | number | null;
}

// A dish with several sizes carries a "12 / 18" style price. The first
// segment is what the menu card leads with, so that is what the cart
// shows. Display only — the backend derives the real charge from the CMS.
function parsePrice(price: string | number | null | undefined): number {
    if (typeof price === 'number') return Number.isFinite(price) ? price : 0;
    if (typeof price !== 'string') return 0;
    const first = Number.parseFloat(price.split('/')[0].trim());
    return Number.isFinite(first) ? first : 0;
}

export function useCart() {
    const dispatch = useAppDispatch();
    const { menuId, items } = useAppSelector((state) => state.cart);

    const count = useMemo(
        () => items.reduce((sum, item) => sum + item.quantity, 0),
        [items]
    );
    const total = useMemo(
        () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
        [items]
    );

    const attachMenu = useCallback(
        (id: number) => { dispatch(setCartMenu(id)); },
        [dispatch]
    );

    const add = useCallback(
        (dish: OrderableDish) => {
            dispatch(addItem({
                dishId:   dish.id,
                name:     dish.name,
                price:    parsePrice(dish.price),
                quantity: 1,
            }));
        },
        [dispatch]
    );

    const setQuantity = useCallback(
        (dishId: number, quantity: number) => { dispatch(updateQuantity({ dishId, quantity })); },
        [dispatch]
    );

    const remove = useCallback(
        (dishId: number) => { dispatch(removeItem(dishId)); },
        [dispatch]
    );

    const quantityOf = useCallback(
        (dishId: number) => items.find((item) => item.dishId === dishId)?.quantity ?? 0,
        [items]
    );

    const clear = useCallback(() => { dispatch(clearCart()); }, [dispatch]);

    return { menuId, items, count, total, attachMenu, add, setQuantity, remove, quantityOf, clear };
}
